if (!customElements.get('details-modal')) {
  customElements.define(
    'details-modal',
    class DetailsModal extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        this.summary = this.querySelector('summary');
        this.panel = this.querySelector('[role="dialog"]');
        this.summary.addEventListener('click', (event) => {
          event.preventDefault();
          this.details.open ? this.close() : this.open();
        });
        this.querySelectorAll('[data-search-close]').forEach((b) => b.addEventListener('click', () => this.close()));
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.details.open) {
            event.stopPropagation();
            this.close();
          }
        });
        this.onOutside = (event) => {
          if (!this.contains(event.target)) this.close(false);
        };
      }

      open() {
        this.details.open = true;
        this.summary.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onOutside);
        const input = this.querySelector('input[type="search"]');
        window.theme.trapFocus(this.panel, input);
      }

      close(returnFocus = true) {
        this.details.open = false;
        this.summary.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onOutside);
        window.theme.releaseFocus(returnFocus ? this.summary : null, this.panel);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.reset = this.querySelector('[type="reset"]');
        this.results = this.querySelector('[data-predictive-results]');
        this.status = this.querySelector('[data-predictive-status]');
        this.cache = new Map();
        this.input.addEventListener('input', () => this.toggleReset());
        this.reset.addEventListener('click', () => {
          setTimeout(() => {
            this.toggleReset();
            this.close();
            this.input.focus();
          });
        });
        this.toggleReset();
        if (this.hasAttribute('data-disabled') || !this.results) return;
        this.input.addEventListener('input', window.theme.debounce(() => this.onInput(), 250));
        this.input.addEventListener('focus', () => this.input.value.trim() && this.onInput());
        this.addEventListener('keydown', (event) => this.onKeydown(event));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) setTimeout(() => this.close(), 120);
        });
      }

      toggleReset() {
        this.reset.hidden = !this.input.value;
      }

      async onInput() {
        const terms = this.input.value.trim();
        if (!terms) return this.close();
        if (this.cache.has(terms)) return this.render(this.cache.get(terms));
        this.results.classList.add('is-loading');
        this.controller && this.controller.abort();
        this.controller = new AbortController();
        try {
          const url = new URL(window.theme.routes.predictiveSearch, window.location.origin);
          url.searchParams.set('q', terms);
          url.searchParams.set('section_id', 'predictive-search');
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('resources[limit_scope]', 'each');
          const response = await fetch(url.toString(), { signal: this.controller.signal });
          if (!response.ok) throw new Error(response.status);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('[data-predictive-content]');
          const markup = content ? content.outerHTML : '';
          this.cache.set(terms, markup);
          if (this.input.value.trim() === terms) this.render(markup);
        } catch (error) {
          if (error.name !== 'AbortError') this.close();
        } finally {
          this.results.classList.remove('is-loading');
        }
      }

      render(markup) {
        this.results.innerHTML = markup;
        this.results.hidden = !markup;
        this.input.setAttribute('aria-expanded', String(Boolean(markup)));
        const content = this.results.querySelector('[data-predictive-content]');
        if (this.status && content) {
          const count = Number(content.dataset.count) || 0;
          this.status.textContent = String(count);
        }
      }

      close() {
        if (!this.results) return;
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }

      onKeydown(event) {
        if (!this.results || this.results.hidden) return;
        const items = Array.from(this.results.querySelectorAll('[role="option"]'));
        if (!items.length) return;
        const current = items.findIndex((i) => i.getAttribute('aria-selected') === 'true');
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const next = event.key === 'ArrowDown' ? (current + 1) % items.length : (current - 1 + items.length) % items.length;
          items.forEach((item, i) => item.setAttribute('aria-selected', String(i === next)));
          this.input.setAttribute('aria-activedescendant', items[next].id);
          items[next].scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter' && current > -1) {
          event.preventDefault();
          const link = items[current].querySelector('a, button');
          link && link.click();
        } else if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      }
    }
  );
}

if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-mega-trigger]');
        this.toggle = this.querySelector('[data-mega-toggle]');
        this.panel = this.querySelector('[data-mega-panel]');
        this.wrapper = this.closest('[data-header]');
        this.desktop = window.matchMedia('(min-width: 990px)');
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && this.desktop.matches) this.schedule(true, 80);
        });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.schedule(false, 120);
        });
        this.toggle.addEventListener('click', () => (this.isOpen ? this.close(true) : this.open()));
        this.trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.open();
          const first = this.panel.querySelector('a');
          if (first) first.focus();
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.isOpen) {
            event.stopPropagation();
            this.close(true);
          }
        });
        this.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
        });
        this.desktop.addEventListener('change', () => this.close());
        document.addEventListener('mega-menu:open', (event) => {
          if (event.detail !== this) this.close();
        });
      }

      get isOpen() {
        return this.classList.contains('is-open');
      }

      schedule(open, delay) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => (open ? this.open() : this.close()), delay);
      }

      open() {
        clearTimeout(this.timer);
        if (this.isOpen || !this.desktop.matches) return;
        document.dispatchEvent(new CustomEvent('mega-menu:open', { detail: this }));
        this.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.toggle.setAttribute('aria-expanded', 'true');
        if (this.wrapper) this.wrapper.setAttribute('data-mega-open', '');
        this.onDocumentKeydown = this.onDocumentKeydown || ((event) => {
          if (event.key === 'Escape') this.close(this.contains(document.activeElement));
        });
        document.addEventListener('keydown', this.onDocumentKeydown);
        this.panel.querySelectorAll('video').forEach((video) => {
          const playing = video.play();
          if (playing) playing.catch(() => {});
        });
      }

      close(returnFocus = false) {
        clearTimeout(this.timer);
        if (!this.isOpen) return;
        this.classList.remove('is-open');
        if (this.onDocumentKeydown) document.removeEventListener('keydown', this.onDocumentKeydown);
        this.trigger.setAttribute('aria-expanded', 'false');
        this.toggle.setAttribute('aria-expanded', 'false');
        if (this.wrapper && !this.wrapper.querySelector('mega-menu.is-open')) this.wrapper.removeAttribute('data-mega-open');
        this.panel.querySelectorAll('video').forEach((video) => video.pause());
        if (returnFocus) this.trigger.focus();
      }
    }
  );
}
